import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { PanelToggleButton } from '@heidi/design-system/panel-toggle-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import { TypographyCaption, TypographyH3 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, Ellipsis, X } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import { ObjectWorkspaceFacetContent } from './ObjectWorkspaceFacetContent';
import { ObjectWorkspacePatientIdentityRow } from './ObjectWorkspacePatientIdentityRow';
import { ObjectWorkspaceSummaryEditor } from './ObjectWorkspaceSummaryEditor';
import {
  FACETS_BY_KIND,
  type ObjectWorkspaceAnchor,
  type ObjectWorkspaceFacet,
  type ObjectWorkspaceObject,
  type ObjectWorkspacePatientDetailsState,
  type ObjectWorkspaceWorkEngagement,
} from './objectWorkspace.data';

interface ObjectWorkspacePaneProps {
  object: ObjectWorkspaceObject;
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  /** Host-owned summary; omitted means empty (no prototype clinical copy). */
  summary?: string;
  isCollapsed?: boolean;
  onExpand?: () => void;
  switcherOptions: readonly ObjectWorkspaceObject[];
  onClose: () => void;
  onObjectChange: (object: ObjectWorkspaceObject) => void;
  onAddPatient?: (anchorElement: HTMLElement) => void;
  onFacetChange?: (facet: ObjectWorkspaceFacet) => void;
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onWorkEngage?: (work: ObjectWorkspaceWorkEngagement) => void;
  onMoreActions?: () => void;
}

const OBJECT_PANE_SPRING = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 34,
  mass: 0.68,
};

export function ObjectWorkspacePane({
  object,
  patientDetailsState,
  summary = '',
  isCollapsed = false,
  onExpand,
  switcherOptions,
  onClose,
  onObjectChange,
  onAddPatient,
  onFacetChange,
  onAnchorSelect,
  onWorkEngage,
  onMoreActions,
}: ObjectWorkspacePaneProps): JSX.Element {
  const intl = useIntl();
  const reducedMotion = useReducedMotion();
  const [activeFacet, setActiveFacet] = useState<ObjectWorkspaceFacet>('work');
  const facets = FACETS_BY_KIND[object.kind];
  const paneLabel = intl.formatMessage(
    object.kind === 'patient' ? messages.patientObjectPane : messages.visitObjectPane,
    { name: object.name }
  );
  const expandLabel = intl.formatMessage(messages.expandObjectPane, {
    name: object.name,
  });
  return (
    <m.aside
      aria-label={paneLabel}
      initial={false}
      layout={reducedMotion ? false : 'size'}
      transition={reducedMotion ? { duration: 0 } : OBJECT_PANE_SPRING}
      className={cn(
        'relative size-full shrink-0 overflow-hidden bg-surface motion-reduce:transform-none md:rounded-2xl md:shadow-sm',
        isCollapsed
          ? 'md:w-20 md:min-w-20 md:max-w-20'
          : 'md:w-[36%] md:min-w-80 md:max-w-[440px]'
      )}
      data-collapsed={isCollapsed}
      data-testid="object-workspace-pane"
    >
      <Tabs
        value={activeFacet}
        onValueChange={(value) => {
          const facet = value as ObjectWorkspaceFacet;
          setActiveFacet(facet);
          onFacetChange?.(facet);
        }}
        className={cn(
          'scrollbar-subtle flex h-full min-h-full flex-col overflow-y-auto',
          isCollapsed && 'pointer-events-none invisible'
        )}
        aria-hidden={isCollapsed}
      >
        <div className="sticky top-0 z-10 bg-surface px-6 pb-4 pt-6 md:rounded-t-2xl">
          <div className="min-w-0">
            {object.kind === 'visit' ? (
              <TypographyCaption className="mb-1 text-text-tertiary">
                {intl.formatMessage(messages.encounterEyebrow)}
              </TypographyCaption>
            ) : null}
            <div className="flex min-w-0 items-start gap-1">
              <div
                className="flex min-w-0 items-start gap-1"
                data-testid="object-workspace-title-group"
              >
                <TypographyH3
                  className="min-w-0 truncate text-text-primary"
                  title={object.name}
                >
                  {object.name}
                </TypographyH3>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconOnly
                      className="shrink-0 [&_svg]:!size-5"
                      aria-label={intl.formatMessage(messages.switchObject, {
                        name: object.name,
                      })}
                    >
                      <ChevronDown aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-64">
                    {switcherOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.id}
                        selected={option.id === object.id}
                        onSelect={() => onObjectChange(option)}
                      >
                        <span className="truncate">{option.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div
                className="ms-auto flex shrink-0 items-start gap-1"
                data-testid="object-workspace-header-actions"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={intl.formatMessage(messages.moreActions)}
                  onClick={onMoreActions}
                >
                  <Ellipsis aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={intl.formatMessage(messages.closeWorkspace)}
                  onClick={onClose}
                >
                  <X aria-hidden />
                </Button>
              </div>
            </div>
            {object.kind === 'visit' ? (
              <ObjectWorkspacePatientIdentityRow
                patient={object.patient}
                onObjectChange={onObjectChange}
                onAddPatient={onAddPatient}
              />
            ) : null}
          </div>

          <div className="mt-3">
            <ObjectWorkspaceSummaryEditor initialSummary={summary} />
          </div>

          <div className="mt-8" data-testid="object-workspace-facet-tabs">
            <TabsList className="h-7 w-full justify-start gap-1 bg-transparent p-0">
              {facets.map((facet) => (
                <TabsTrigger
                  key={facet.id}
                  value={facet.id}
                  className="h-7 px-2 data-[state=active]:bg-background-secondary"
                >
                  {intl.formatMessage(facet.label)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        {facets.map((facet) => (
          <TabsContent key={facet.id} value={facet.id} className="m-0 px-6 pb-8 pt-2">
            <ObjectWorkspaceFacetContent
              facet={facet.id}
              kind={object.kind}
              patientDetailsState={patientDetailsState}
              onAnchorSelect={onAnchorSelect}
              onWorkEngage={onWorkEngage}
            />
          </TabsContent>
        ))}
      </Tabs>

      {isCollapsed ? (
        <div className="scrollbar-subtle absolute inset-0 flex flex-col items-center gap-2 overflow-y-auto px-2 py-4">
          <PanelToggleButton
            type="button"
            open={false}
            side="left"
            variant="ghost"
            aria-label={expandLabel}
            onClick={onExpand}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto min-h-8 w-full min-w-0 whitespace-normal px-1 py-2 text-center"
            title={object.name}
            onClick={onExpand}
          >
            {object.name}
          </Button>
        </div>
      ) : null}
    </m.aside>
  );
}
